/* eslint-disable react/require-default-props */
import React, { useEffect, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { useDispatch, useSelector } from 'react-redux';
import tabId from 'services/tabId';
import { ApplicationState } from 'store';
import {
  BiddingChatState,
  BiddingChatTypes,
  BiddingChatMessageTypes,
} from 'store/modules/bidding_chat/types';
import { MenusBehaviourTypes } from 'store/modules/menus_behaviour_store/types';
import UserQuestion from 'components/molecules/bidding_chat_user_questions';
import StrategicQuestionsPanel from 'components/molecules/bidding_chat_strategic_questions';
import BiddingChatConversation from 'components/molecules/bidding_chat_conversation';
import { useBiddingChatSession } from 'hooks/useBiddingChatSession';
import createTrack from 'services/actions/tracker_actions';
import iconSummary from 'assets/images/icon_summary.svg';
import { BiddingSummaryTypes } from 'store/modules/bidding_summary_store/types';
import { BiddingChatDrawerStyles, Container } from './styles';

type QuestionDrawerKind = null | 'saved' | 'strategic';

interface BiddingChatProps {
  showModal: boolean;
  biddingId: number;
  isSummaryOpen?: boolean;
}

const BIDDING_CHAT_WELCOME_MESSAGE: BiddingChatMessageTypes = {
  id: null,
  bidding_user_chat_id: null,
  role: 'assistant',
  content:
    '<p>Boas-vindas ao Pergunte ao Edital, uma inteligência artificial preparada para te auxiliar com todas as informações relacionadas a esta licitação.</p><p><strong>Instruções de uso</strong>: Para aproveitar ao máximo esta ferramenta, faça perguntas claras e específicas, fornecendo o máximo de contexto possível. Evite fazer perguntas com somente uma palavra.</p><p class="mb-0">Então vamos lá, como posso colaborar hoje?</p>',
  rating: null,
  created_at: '',
  updated_at: '',
};

const BiddingChat = (props: BiddingChatProps) => {
  const { showModal, biddingId, isSummaryOpen = false } = props;
  const tab = tabId();
  const [questionDrawer, setQuestionDrawer] = useState<QuestionDrawerKind>(null);
  const previousSummaryBiddingOpenRef = useRef(0);

  const { bidding_chat } = useSelector<ApplicationState, BiddingChatState>(
    (state) => state.session.bidding_chat,
  );

  const dispatch = useDispatch();

  const session = useBiddingChatSession({ biddingId, active: showModal });
  const { isLoadingMessage, sendMessage } = session;

  const showQuestionsDrawer = questionDrawer !== null;
  const isSavedDrawer = questionDrawer === 'saved';
  const isStrategicDrawer = questionDrawer === 'strategic';
  const currentChat = bidding_chat.current_chat[tab];

  const closeChat = () => {
    dispatch({ type: BiddingChatTypes.CLOSE_CHAT });
    setQuestionDrawer(null);
  };

  const closeQuestionsDrawer = () => setQuestionDrawer(null);

  const closeChatAndSummary = () => {
    closeChat();

    if (isSummaryOpen) {
      dispatch({ type: BiddingSummaryTypes.CLOSE_BIDDING_SUMMARY });
      dispatch({ type: BiddingSummaryTypes.CLEAR_BIDDING_SUMMARY_DATA });
    }
  };

  const openBiddingSummaryFromChat = () => {
    if (biddingId === 0) return;

    if (
      previousSummaryBiddingOpenRef.current !== biddingId &&
      previousSummaryBiddingOpenRef.current !== 0
    ) {
      dispatch({ type: BiddingSummaryTypes.CLEAR_CURRENT_BIDDING_SUMMARY });
    }
    previousSummaryBiddingOpenRef.current = biddingId;

    dispatch({
      type: BiddingSummaryTypes.OPEN_BIDDING_SUMMARY,
      biddingId,
      editalDownload: currentChat?.editalDownload,
      capagInfo: currentChat?.capagInfo,
    });
    createTrack('Resumo do Edital - Clicou no botão');
  };

  const openFeedBackModal = () => {
    closeChat();
    dispatch({ type: MenusBehaviourTypes.OPEN_FEEDBACK_MODAL, pathname: 'Pergunte ao edital' });
  };

  useEffect(() => {
    if (isLoadingMessage) setQuestionDrawer(null);
  }, [isLoadingMessage]);

  return (
    <>
      <BiddingChatDrawerStyles />
      <Modal
        onHide={closeChatAndSummary}
        show={showModal}
        centered={false}
        enforceFocus={false}
        className={`bidding-chat-drawer-modal${
          isSummaryOpen ? ' bidding-chat-drawer-modal--with-summary' : ''
        }`}
        backdropClassName={`bidding-chat-drawer-backdrop ${
          isSummaryOpen ? 'bidding-chat-drawer-backdrop--with-summary' : ''
        }`}
        dialogClassName="bidding-chat-drawer-dialog"
        contentClassName="bidding-chat-drawer-content"
        aria-labelledby="bidding-chat-drawer-title"
      >
        {showModal && biddingId !== 0 && !isSummaryOpen ? (
          <span className="bidding-chat-summary-fab-anchor">
            <span className="bidding-chat-summary-fab-tooltip" aria-hidden="true">
              Resumo do Edital
            </span>
            <button
              type="button"
              className="bidding-chat-summary-fab"
              onClick={() => openBiddingSummaryFromChat()}
              aria-label="Resumo do Edital"
            >
              <img src={iconSummary} alt="" aria-hidden />
            </button>
          </span>
        ) : null}
        <Container className="bidding-chat-container">
          <div
            className="bidding-chat-dismiss-saved-drawer-zone"
            role="presentation"
            onClick={() => {
              if (questionDrawer !== null) setQuestionDrawer(null);
            }}
          >
            <div className="modal-header bidding-chat-drawer-header">
              <div className="modal-title d-flex w-100 align-items-center flex-wrap">
                <div className="mr-auto mb-2 mb-md-0 bidding-chat-header-text">
                  <h5 id="bidding-chat-drawer-title" className="mb-0">
                    Pergunte ao Edital
                  </h5>
                  <small>Nº Conlicitação: {biddingId}</small>
                </div>
                <div className="d-flex align-items-center bidding-chat-header-toolbar flex-wrap">
                  <button
                    type="button"
                    className={`btn mb-2 mb-md-0 bidding-chat-panel-toggle ${
                      isSavedDrawer
                        ? 'bidding-chat-panel-toggle--saved-active'
                        : 'btn-outline-secondary'
                    }`}
                    onClick={() =>
                      setQuestionDrawer((panel) => (panel === 'saved' ? null : 'saved'))
                    }
                    disabled={isLoadingMessage}
                    aria-expanded={isSavedDrawer}
                    aria-controls="bidding-chat-questions-drawer"
                  >
                    Perguntas Salvas
                  </button>
                  <button
                    type="button"
                    className={`btn mb-2 mb-md-0 bidding-chat-panel-toggle bidding-chat-panel-toggle--strategic-active border-0${
                      isStrategicDrawer ? ' bidding-chat-panel-toggle--strategic-drawer-open' : ''
                    }`}
                    onClick={() =>
                      setQuestionDrawer((panel) => (panel === 'strategic' ? null : 'strategic'))
                    }
                    disabled={isLoadingMessage}
                    aria-expanded={isStrategicDrawer}
                    aria-controls="bidding-chat-questions-drawer"
                  >
                    Perguntas Estratégicas
                  </button>
                  <button
                    aria-label="Close"
                    className="close mx-0 bidding-chat-header-close mb-2 mb-md-0"
                    data-dismiss="modal"
                    type="button"
                    onClick={closeChat}
                  >
                    <span aria-hidden="true">×</span>
                  </button>
                </div>
              </div>
            </div>
            <BiddingChatConversation
              session={session}
              welcomeMessage={BIDDING_CHAT_WELCOME_MESSAGE}
              footerFeedbackAction={
                <button
                  type="button"
                  className="chatbot-footer-feedback-btn"
                  onClick={() => openFeedBackModal()}
                >
                  Deixe seu feedback sobre esta ferramenta
                </button>
              }
            />
          </div>
          <aside
            id="bidding-chat-questions-drawer"
            className={`bidding-chat-saved-drawer ${showQuestionsDrawer ? 'is-open' : ''}`}
            aria-hidden={!showQuestionsDrawer}
          >
            <div className="bidding-chat-saved-drawer-header">
              <h6 className="mb-0 bidding-chat-saved-drawer-title">
                {isStrategicDrawer ? 'Perguntas estratégicas' : 'Perguntas Salvas'}
              </h6>
              <button
                type="button"
                className="bidding-chat-saved-drawer-close"
                aria-label={
                  isStrategicDrawer ? 'Fechar perguntas estratégicas' : 'Fechar perguntas salvas'
                }
                onClick={closeQuestionsDrawer}
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
            {isSavedDrawer ? (
              <UserQuestion onCloseDrawer={closeQuestionsDrawer} sendMessage={sendMessage} />
            ) : null}
            {isStrategicDrawer ? (
              <StrategicQuestionsPanel
                onCloseDrawer={closeQuestionsDrawer}
                sendMessage={sendMessage}
              />
            ) : null}
          </aside>
        </Container>
      </Modal>
    </>
  );
};

export default BiddingChat;
